/**
 * Destroying an account's local partition.
 *
 * Sign-out has to erase, not merely forget: an account's IndexedDB database
 * and OPFS directory hold a full, readable copy of the vault, and on a shared
 * or lost machine that copy is the whole vault. The dependencies are injected
 * so the erase can be proven without a browser.
 */
export interface PartitionStorage {
  readonly indexedDB: Pick<IDBFactory, "deleteDatabase">;
  readonly getDirectory?: () => Promise<FileSystemDirectoryHandle>;
}

export function databaseName(partition: string): string {
  return `recallvault:${partition}`;
}

export async function partitionKey(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].slice(0, 12).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function erasePartition(partition: string, storage: PartitionStorage): Promise<void> {
  await deleteDatabase(partition, storage.indexedDB);
  await deleteOpfsPartition(partition, storage.getDirectory);
}

function deleteDatabase(partition: string, factory: PartitionStorage["indexedDB"]): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = factory.deleteDatabase(databaseName(partition));
    request.onsuccess = () => resolve();
    // A blocked delete still runs once the last connection closes; the caller
    // has already dropped its own, so waiting here would only hang sign-out.
    request.onblocked = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("IndexedDB delete failed"));
  });
}

async function deleteOpfsPartition(
  partition: string,
  getDirectory: PartitionStorage["getDirectory"],
): Promise<void> {
  if (!getDirectory) return;
  try {
    const root = await getDirectory();
    const accounts = await root.getDirectoryHandle("accounts");
    await accounts.removeEntry(partition, { recursive: true });
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "NotFoundError")) throw error;
  }
}
